import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, StatusChip, LoadingBlock } from "@/components/Common";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus } from "lucide-react";
import { toast } from "sonner";

const COLUMNS = ["new", "assigned", "in_progress", "waiting", "done"];

export default function Maintenance() {
    const [items, setItems] = useState(null);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({
        title: "",
        description: "",
        category: "general",
        severity: "med",
        room_number: "",
    });

    const load = async () => {
        const { data } = await api.get("/maintenance");
        setItems(data);
    };
    useEffect(() => {
        load();
    }, []);

    const create = async () => {
        if (!form.title) return toast.error("Title required");
        await api.post("/maintenance", form);
        toast.success("Ticket created");
        setForm({ title: "", description: "", category: "general", severity: "med", room_number: "" });
        setOpen(false);
        load();
    };

    const setStatus = async (t, status) => {
        await api.patch(`/maintenance/${t.id}`, { status });
        toast.success(`Ticket → ${status}`);
        load();
    };

    if (!items) return <LoadingBlock rows={6} />;
    const grouped = COLUMNS.reduce((acc, k) => ((acc[k] = items.filter((t) => t.status === k)), acc), {});

    return (
        <>
            <PageHeader
                title="Maintenance"
                subtitle="Triage and route tickets across categories."
                actions={
                    <Button onClick={() => setOpen(true)} data-testid="maint-new">
                        <Plus className="w-4 h-4 mr-1" /> New ticket
                    </Button>
                }
            />
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4" data-testid="maint-board">
                {COLUMNS.map((c) => (
                    <div key={c} className="rounded-xl border border-border bg-card" data-testid={`maint-col-${c}`}>
                        <div className="px-3 py-2 border-b border-border flex items-center justify-between">
                            <div className="text-sm font-medium capitalize">{c.replace("_", " ")}</div>
                            <span className="text-xs text-muted-foreground tabular">{grouped[c].length}</span>
                        </div>
                        <div className="p-3 space-y-2 min-h-[60px]">
                            {grouped[c].map((t) => (
                                <div key={t.id} className="rounded-lg border border-border bg-background p-3" data-testid="maint-card">
                                    <div className="flex items-center justify-between mb-1">
                                        <div className="font-medium text-sm pr-2">{t.title}</div>
                                        <StatusChip status={t.severity} label={t.severity} />
                                    </div>
                                    <div className="text-xs text-muted-foreground line-clamp-2 mb-2">{t.description}</div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-[11px] text-muted-foreground">
                                            {t.category} · Room {t.room_number || "—"}
                                        </span>
                                        <Select value={t.status} onValueChange={(v) => setStatus(t, v)}>
                                            <SelectTrigger className="h-7 w-32" data-testid="maint-set-status">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {COLUMNS.map((s) => (
                                                    <SelectItem key={s} value={s} className="capitalize">
                                                        {s.replace("_", " ")}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Create maintenance ticket</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label>Title</Label>
                            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="maint-title" />
                        </div>
                        <div>
                            <Label>Description</Label>
                            <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                            <div>
                                <Label>Category</Label>
                                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {["general", "hvac", "plumbing", "electrical", "safety"].map((c) => (
                                            <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Severity</Label>
                                <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {["low", "med", "high"].map((c) => (
                                            <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Room</Label>
                                <Input value={form.room_number} onChange={(e) => setForm({ ...form, room_number: e.target.value })} placeholder="optional" />
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                        <Button onClick={create} data-testid="maint-create">Create</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
